/**
 * Normalize gradient stop colors for `react-native-svg` LinearGradient stops.
 * Android SVG often ignores alpha inside `stopColor` / mishandles `transparent`.
 * Always split RGB + opacity for reliable fades on iOS and Android.
 */
export type SvgGradientStop = {
  stopColor: string;
  stopOpacity: number;
};

export function parseSvgGradientStop(color: string): SvgGradientStop {
  const trimmed = color.trim();
  if (!trimmed || trimmed.toLowerCase() === 'transparent') {
    return { stopColor: 'rgb(0, 0, 0)', stopOpacity: 0 };
  }

  const eight_digit = /^#([0-9a-f]{6})([0-9a-f]{2})$/i.exec(trimmed);
  if (eight_digit) {
    const hex = eight_digit[1];
    const alpha = parseInt(eight_digit[2], 16) / 255;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return { stopColor: `rgb(${r}, ${g}, ${b})`, stopOpacity: alpha };
  }

  const rgba = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i.exec(
    trimmed,
  );
  if (rgba) {
    const r = rgba[1];
    const g = rgba[2];
    const b = rgba[3];
    const a = rgba[4] != null ? Number(rgba[4]) : 1;
    return {
      stopColor: `rgb(${r}, ${g}, ${b})`,
      stopOpacity: Number.isFinite(a) ? a : 1,
    };
  }

  return { stopColor: trimmed, stopOpacity: 1 };
}

/** Prefer `parseSvgGradientStop` when opacity must be a separate SVG stop attribute. */
export function normalizeSvgStopColor(color: string): string {
  const { stopColor, stopOpacity } = parseSvgGradientStop(color);
  if (stopOpacity >= 1) {
    return stopColor;
  }
  return stopColor.replace(/^rgb\(/, 'rgba(').replace(/\)$/, `, ${stopOpacity})`);
}

/** Normalize every stop before passing to `AppLinearGradient`. */
export function normalizeGradientColors(colors: readonly string[]): string[] {
  return colors.map(normalizeSvgStopColor);
}
